import type { WorkspaceDataStatus } from "@adpilot/shared";
import {
  IconChevronDown,
  IconPlayerPause,
  IconPlayerPlay,
  IconRefresh,
  IconTrash,
} from "@tabler/icons-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { TableCell, TableRow } from "@/components/ui/table";
import { type AmazonAuthorizationRow, marketplaceDisplayName } from "@/lib/amazon-authorization";
import { cn } from "@/lib/utils";
import { ShopDataHistory } from "./ShopDataHistory";

export function AuthorizationRow({
  row,
  dataStatus,
  dataStatusLoading,
  dataStatusError,
  defaultDataDetails = false,
  onRefreshShop,
  refreshingShopId,
  canManage,
  onRequestDeactivate,
  onRequestDelete,
  onSetShopActivation,
  changingActivationShopId,
  deleting = false,
}: {
  row: AmazonAuthorizationRow;
  dataStatus?: WorkspaceDataStatus["shops"][number];
  dataStatusLoading?: boolean;
  dataStatusError?: boolean;
  defaultDataDetails?: boolean;
  onRefreshShop?: (shopId: string) => void;
  refreshingShopId?: string | null;
  canManage: boolean;
  onRequestDeactivate: (row: AmazonAuthorizationRow) => void;
  onRequestDelete?: (row: AmazonAuthorizationRow) => void;
  onSetShopActivation: (shopId: string, isActive: boolean) => void;
  changingActivationShopId: string | null;
  deleting?: boolean;
}): React.ReactElement {
  const { i18n, t } = useTranslation("settings");
  const [expanded, setExpanded] = useState(defaultDataDetails);
  const shopName = `${row.shop.shopName} · ${marketplaceDisplayName(row.shop.marketplaceName, i18n.resolvedLanguage ?? i18n.language)}`;
  const busy = deleting || changingActivationShopId === row.shop.shopId;
  // A vendor (1P) shop has no Seller Central sales until Vendor Central lands (#679),
  // so its summary reads from Ads alone instead of waiting on Sales forever. Decided
  // by account type like the server's data status, even if an SP-API grant is linked.
  const vendor = row.shop.accountType === "vendor";
  const expectedSources = vendor ? 1 : 2;
  const reportSources =
    dataStatus?.sources.filter(
      (source) => source.source === "ads" || (!vendor && source.source === "sales"),
    ) ?? [];
  const summaryKey = !row.shop.isActive
    ? "dataStatus.states.paused"
    : dataStatusError
      ? "dataStatus.states.unavailable"
      : reportSources.some((source) => source.state === "action_required")
        ? "dataStatus.states.action_required"
        : reportSources.some(
              (source) => source.state === "updating" || source.state === "preparing",
            )
          ? "dataStatus.states.updating"
          : reportSources.length === expectedSources &&
              reportSources.every((source) => source.state === "ready")
            ? "dataStatus.shopReady"
            : reportSources.length === expectedSources
              ? "dataStatus.shopPending"
              : "dataStatus.automatic";

  return (
    <Fragment>
      <TableRow className={cn(!row.shop.isActive && "bg-canvas/55 text-muted-foreground")}>
        <TableCell>
          <span className="block max-w-[130px] truncate text-muted-foreground">
            {marketplaceDisplayName(
              row.shop.marketplaceName,
              i18n.resolvedLanguage ?? i18n.language,
            )}
          </span>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Badge variant={row.shop.isActive ? "success" : "neutral"}>
              {t(
                row.shop.isActive
                  ? "amazonAuthorization.table.active"
                  : "amazonAuthorization.table.paused",
              )}
            </Badge>
            {canManage ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      size="icon-xs"
                      variant="ghost"
                      aria-label={t("amazonAuthorization.table.manageShop", {
                        name: row.shop.shopName,
                      })}
                      title={t("amazonAuthorization.table.shopStatus")}
                      disabled={busy}
                      aria-busy={busy}
                    />
                  }
                >
                  {busy ? (
                    <Spinner aria-hidden />
                  ) : row.shop.isActive ? (
                    <IconPlayerPlay aria-hidden />
                  ) : (
                    <IconPlayerPause aria-hidden />
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-40">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      disabled={row.shop.isActive || changingActivationShopId === row.shop.shopId}
                      onClick={() => onSetShopActivation(row.shop.shopId, true)}
                    >
                      <IconPlayerPlay aria-hidden />
                      {t("amazonAuthorization.table.activate")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={!row.shop.isActive || changingActivationShopId === row.shop.shopId}
                      variant="destructive"
                      onClick={() => onRequestDeactivate(row)}
                    >
                      <IconPlayerPause aria-hidden />
                      {t("amazonAds.deactivate")}
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  {onRequestDelete ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        <DropdownMenuItem
                          disabled={busy}
                          variant="destructive"
                          onClick={() => onRequestDelete(row)}
                        >
                          <IconTrash aria-hidden />
                          {t("amazonAuthorization.delete.site")}
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </TableCell>
        <TableCell className="capitalize text-muted-foreground">
          {row.shop.accountType ?? t("amazonAuthorization.table.unavailable")}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
          >
            {dataStatusLoading ? (
              <Spinner className="size-4" aria-label={t("dataStatus.loading")} />
            ) : (
              t(summaryKey)
            )}
            <IconChevronDown
              className={cn("size-4 transition-transform", expanded && "rotate-180")}
              aria-hidden
            />
          </Button>
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow>
          <TableCell colSpan={4} className="whitespace-normal bg-surface px-4 py-4">
            {dataStatusLoading ? (
              <Spinner className="size-4" aria-label={t("dataStatus.loading")} />
            ) : null}
            {dataStatusError ? (
              <span className="text-xs text-muted-foreground">
                {t("dataStatus.statusErrorShort")}
              </span>
            ) : null}
            {dataStatus ? (
              <div className="flex flex-col gap-4">
                <ShopDataHistory
                  shop={dataStatus}
                  shopName={shopName}
                  unavailable={dataStatusError}
                  vendor={vendor}
                />
                {canManage && onRefreshShop ? (
                  <div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={
                        refreshingShopId != null ||
                        dataStatus.sources.every((source) => source.state === "paused")
                      }
                      onClick={() => onRefreshShop(row.shop.shopId)}
                    >
                      {refreshingShopId === row.shop.shopId ? (
                        <Spinner data-icon="inline-start" aria-hidden />
                      ) : (
                        <IconRefresh aria-hidden />
                      )}
                      {t("dataStatus.refreshShort")}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </TableCell>
        </TableRow>
      ) : null}
    </Fragment>
  );
}
